import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styles from './SessionModal.css';
import SessionLogout from './SessionLogout';
import { logoutUtil, refreshTokens, isBrokerLogin } from 'lib/utils/common';
import { LOGIN_CAPTURE_PATH } from 'shared/constants/paths';
import { useOktaAuth } from '@okta/okta-react';
import LocalStorageProxy, { properties } from 'lib/localStorageProxy';
import UIActions from 'actions/UIActions';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { useSessionWarning } from './useSessionTimeout';
import { SessionCountDown } from './SessionCountDown';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const SessionModal = (props) => {
  const history = useHistory();
  const { oktaAuth } = useOktaAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showContinueSession, setShowContinueSession] = useState(
    !!LocalStorageProxy.showContinueSession,
  );

  useEffect(() => {
    /*
      if the user is logged in and the showContinueSession is true,
      we need to clear the showContinueSession and set the sessionStatus to false
    */
    if (LocalStorageProxy.showContinueSession && LocalStorageProxy.token) {
      LocalStorageProxy.clear(properties.SHOW_CONTINUE_SESSION);
      setShowContinueSession({
        showContinueSession: false,
      });
    }
  }, []);

  useSessionWarning({
    oktaAuth,
    isTrigger: !props.isCountDown && !isBrokerLogin(),
    onWarning: (timeLeft) => {
      props.setSessionStatus({
        isCountDown: true,
        timeLeft,
      });
    },
  });

  useEffect(() => {
    let timer;
    if (props.isCountDown && props.timeLeft > 1 && !showContinueSession) {
      timer = setInterval(() => {
        props.setSessionStatus({
          ...props.sessionStatus,
          timeLeft: props.timeLeft - 1,
        });
      }, 1000);

      return () => clearInterval(timer);
    }

    if (props.timeLeft <= 1 && !showContinueSession) {
      onLogout({ showContinueSession: true });
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [props.isCountDown, props.timeLeft, showContinueSession]);

  const onContinue = async () => {
    setIsLoading(true);
    try {
      await refreshTokens(oktaAuth);
      setIsLoading(false);
      setShowContinueSession(false);
      LocalStorageProxy.clear(properties.SHOW_CONTINUE_SESSION);
      props.setSessionStatus({
        isCountDown: false,
        isContinue: false,
        timeLeft: 120, // Reset timer when continuing session
      });
    } catch (error) {
      console.error('Continue failed:', error);
      setIsLoading(false);
    }
  };

  const onLogout = async ({ showContinueSession = false }) => {
    setIsLoading(true);

    try {
      await logoutUtil({
        oktaAuth,
        showContinueSession,
      });
      props.setSessionStatus({
        isContinue: showContinueSession,
        isCountDown: false,
        timeLeft: 120,
      });
      setIsLoading(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const onLoginClick = () => {
    LocalStorageProxy.clear(properties.SHOW_CONTINUE_SESSION);
    setShowContinueSession(false);
    props.setSessionStatus({
      isCountDown: false,
      isContinue: false,
    });
    history.push(LOGIN_CAPTURE_PATH);
  };

  if (props.isCountDown && !showContinueSession) {
    return (
      <SessionCountDown
        timeLeft={props.timeLeft}
        onContinue={onContinue}
        isLoading={isLoading}
        onLogout={onLogout}
      />
    );
  }

  if (showContinueSession && !LocalStorageProxy.token) {
    return (
      <div className={styles.modalDialogLogout}>
        <SessionLogout onLoginClick={onLoginClick} />
      </div>
    );
  }

  return null;
};

const mapStateToProps = (state) => {
  return {
    isCountDown: state.UISettings.sessionStatus.isCountDown,
    timeLeft: state.UISettings.sessionStatus.timeLeft,
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      setSessionStatus: UIActions.setSessionStatus,
    },
    dispatch,
  );

SessionModal.propTypes = {
  timeLeft: PropTypes.number.isRequired,
  sessionStatus: PropTypes.shape({
    isContinue: PropTypes.bool,
    isCountDown: PropTypes.bool,
    timeLeft: PropTypes.number,
  }),
  setSessionStatus: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(SessionModal);
